import React, { memo } from 'react';
import { colors, css, fonts} from '../../Styles';
import { progressColor } from '../../Definitions';

import FeedLabel from './SubComponents/feedLabel';
import { getProgressData } from './_ProgressViewer/getProgressData';
import { styled } from 'styletron-react';
import { CircleTypes, Progress_Props, ProgressTypes } from './_ProgressViewer/progressDefinitions';
import { useTranslation } from 'react-i18next';

// #region | Circle Path
const CirclePath = (percent: number, animation: boolean, noData: boolean) => {
  const thisPath = "M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"

  const progressAnimation = {
    "0%": { strokeDasharray: "0, 100" },
    "100%": { strokeDasharray: `${percent}, 100`}
  };

  const AnimatedPath = styled("path", {
    animationName: animation ? progressAnimation : "unset",
    animationDuration: "2s",
    animationTimingFunction: "ease",
    animationFillMode: "forwards"
  });

  return (
    <svg viewBox="0 0 36 36" style={{ display: "block", maxHeight: "120px", maxWidth: "120px" }}>
      <path
        style={{ stroke: colors.greyTertiary, fill: "none", strokeWidth: "3.6px" }}
        d={thisPath}
      />
      {!noData ? <AnimatedPath
        style={{ stroke: progressColor(percent), fill: "none", strokeWidth: "4px", strokeLinecap: "round", strokeDasharray: `${percent}, 100` }}
        d={thisPath}
      /> : undefined}
    </svg>
  )
}
// #endregion

// #region | Extra Path for too much fed
const ExtraPath = () => {
  const thisPath = "M193.953,275.806C195.403,276.358 197.789,275.108 199.917,274.185C231.672,260.409 257.368,235.566 272.206,204.277C273.107,202.376 274.224,200.329 273.791,199.003C273.357,197.676 269.605,196.32 269.605,196.32L256.259,190.699C256.259,190.699 252.573,188.55 251.099,189.104C249.624,189.658 248.36,192.081 247.412,194.022C235.324,218.762 214.83,238.418 189.596,249.461C187.756,250.266 185.56,250.869 185,252.237C184.44,253.604 186.237,257.666 186.237,257.666L191.215,270.868C191.215,270.868 192.502,275.253 193.953,275.806"
  
  return (
    <svg viewBox="0 0 286 286" style={{ display: "block", maxHeight: "160px", maxWidth: "160px" }}>
      <path
        style={{ stroke: colors.systemRed, fill: "none", strokeWidth: "2px", filter: "blur(0.5px)" }}
        d={thisPath}
      />
      <path
        style={{ stroke: colors.systemRed, fill: "none", strokeWidth: "1px"}}
        d={thisPath}
      />
    </svg>
  )
}
// #endregion

interface ProgressCircle_Props extends Progress_Props {
  circleType?: CircleTypes;
  // focusView?: boolean
}

export default function ProgressCircle ({total = 0, progressed = 0, animation = false, labels, extension, circleType = CircleTypes.primary}:ProgressCircle_Props) {
  const { t } = useTranslation();
  const { percent, text } = getProgressData(progressed, total, (circleType === CircleTypes.primary ? ProgressTypes.consumed : ProgressTypes.remaining), circleType);

  // If no data
  const noData = progressed <= 0 && total <= 0;

  // If too much fed
  const tooMuchFed = progressed > total;
  
  const negative = tooMuchFed && circleType === CircleTypes.feed && (
    <FeedLabel width="40px" height={"15px"} labelColor={colors.systemRed} noBorder contentString={`+${Math.abs((total-progressed)/1000).toFixed(2)}`} tooltip/>
  );

  const totalToDisplay = circleType === CircleTypes.feed ? (total/1000).toFixed(2) : total;

  // #region | Progress Circle JSX
  const circle = ( <div style={{ position: "relative" }}>
    <div style={{width: "120px", height: "120px"}}>
       {CirclePath(percent, animation, noData)}
    </div>
    {tooMuchFed && (extension&&extension.hasExtension) && circleType === CircleTypes.feed ? 
      <div style={{width: "160px", height: "160px", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)"}}>
        {ExtraPath()}
        <div style={{ transform: "rotate(-45deg)", transformOrigin: "center center", position: "absolute", right: "10px", bottom: "20px" }}>{negative}</div>
      </div>
    : <></>}
  </div> )
  // #endregion

  return ( 
    <div style={{position: "relative", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "end", gap: "10px", height: "183px", width: "160px"}}>
      <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, textAlign: "center", maxWidth: undefined}}>{labels&&labels.header}</p>
      <div style={{position: "relative", width: "120px", height: "120px"}}>
        {circle}
        <div style={{  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", ...fonts.header }}>
          { noData ? <div style={{...css.emptyPen, opacity: "0.5"}}>{labels&&labels.noDataDisclaimer?labels.noDataDisclaimer: t("noData", { defaultValue: "no data" })}</div> : <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
            <p style={{ ...fonts.fontSize1, color: progressColor(percent), margin: "0px" }}>{`${!tooMuchFed ? text : 0}`}</p>
            <p style={{ ...fonts.fontSize4, color: colors.greyPrimary, margin: "0px" }}>{`/ ${totalToDisplay}`}</p>
          </div>}
        </div>
      </div>
      <div style={{height: "15px", display: "flex", alignItems: "center", justifyContent: "center", width: "100%"}}>{labels&&labels.unit}</div>
    </div> 
  );
};

export const MemoProgressCircle = memo(ProgressCircle)